import React, { useEffect, useState, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { Col, Form, Row } from 'antd';
import { Checkbox, TextArea, Select, DatePicker, Popup, Tooltip } from '_atoms';
import type { SelectOption } from '_atoms/Select';
import { REQUIRED_RULE } from '_organisms/InterviewFeedback/constants';
import { ACTIONS, Can, ROLES, UI } from 'permission';
import { possibleTime } from 'constants/constants';
import { BUTTON_TEXT } from 'constants/text';
import * as actions from 'actions';
import { getUser } from 'store/selectors/authorizeSelectors';
import { useAppDispatch } from 'store';
import { ReactComponent as Info } from 'dist/icons/info.svg';
import './style.scss';
import type { Moment } from 'moment';
import type { Reminder, ReminderPayload } from 'store/reducers/candidateReducer';

interface RecruiterOption extends SelectOption {
  label: string;
  value: string;
  has_calendar_access?: boolean | null;
}

interface ReminderFormValues {
  date: Moment;
  time: string;
  candidate?: string;
  comment: string;
  type?: string;
  create_google_event: boolean;
  recruiter?: string;
}

const reminderType = [
  { value: 'Phone call', label: 'Phone call' },
  { value: 'LinkedIn', label: 'LinkedIn' },
  { value: 'Email', label: 'Email' },
  { value: 'Telegram', label: 'Telegram' },
  { value: 'Viber', label: 'Viber' },
  { value: 'Interview', label: 'Interview' },
  { value: 'Other interaction', label: 'Other interaction' },
];

interface PopupCreateReminderProps {
  open: boolean;
  onClose: () => void;
  data?: Partial<Reminder> | null;
  initials?: string;
}

export const PopupCreateReminder: React.FC<PopupCreateReminderProps> = ({ open, onClose, data, initials }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [form] = Form.useForm();
  const { id } = useParams<{ id: string }>();

  const user = useSelector(getUser);
  const currentUserIsRecruiter =
    user.active_role.name === ROLES.RECRUITER || user.active_role.name === ROLES.RECRUITER_LEAD;

  const [recruiterOptions, setRecruiterOptions] = useState<RecruiterOption[]>([]);

  const initialValues = useMemo(
    () => ({
      date: moment(),
      time: possibleTime[26].value,
      candidate: initials,
      comment: '',
      type: undefined,
      create_google_event: false,
      recruiter: currentUserIsRecruiter ? user.uuid : undefined,
    }),
    [initials, currentUserIsRecruiter, user]
  );

  const onSave = ({ date, time, ...values }: ReminderFormValues) => {
    const hour = Number(time.split(':')[0]);
    const minute = Number(time.split(':')[1]);
    const payload: ReminderPayload = {
      ...values,
      datetime: date.set({ hour, minute, second: 0 }),
      candidate: id,
      in_progress: true,
      create_google_event: !!values.create_google_event,
    };
    dispatch(actions.editReminder(payload, data?.uuid, id)).then(onClose);
  };

  useEffect(() => {
    if (open) {
      actions
        .getListUsersByRole({ role: [ROLES.HR_LEAD, ROLES.RECRUITER_LEAD, ROLES.RECRUITER] })
        .then(resp =>
          setRecruiterOptions(
            (resp || []).map(r => ({
              label: r.full_name,
              value: r.uuid,
              has_calendar_access: r.profile.has_calendar_access,
            }))
          )
        )
        .catch(() => setRecruiterOptions([]));
    } else {
      setRecruiterOptions([]);
      form.resetFields();
    }
  }, [open]);

  useEffect(() => {
    form.resetFields();
  }, [initialValues]);

  useEffect(() => {
    if (data) form.setFieldsValue(data);
  }, [data]);

  return (
    <Popup
      open={open}
      title={
        data ? t('translation:popup_create_reminder.title_edit') : t('translation:popup_create_reminder.title_new')
      }
      cancelButtonProps={{
        onClick: onClose,
        children: BUTTON_TEXT.CANCEL,
      }}
      okButtonProps={{
        children: data ? BUTTON_TEXT.SAVE : BUTTON_TEXT.ADD,
        form: 'form-reminder',
        htmlType: 'submit',
      }}
      className="popup-new-reminder"
    >
      <Form name="form-reminder" form={form} initialValues={initialValues} onFinish={onSave}>
        <Row gutter={[16, 0]}>
          <Col span={12}>
            <DatePicker
              label={t('translation:popup_create_reminder.label_date')}
              itemProps={{ name: 'date', rules: [REQUIRED_RULE] }}
              minDate={moment().startOf('day')}
            />
          </Col>
          <Col span={12}>
            <Select
              label={t('translation:popup_create_reminder.label_time')}
              placeholder={t('translation:popup_create_reminder.placeholder_time')}
              options={possibleTime as SelectOption[]}
              itemProps={{ name: 'time', rules: [REQUIRED_RULE] }}
            />
          </Col>
        </Row>
        <Select
          label={t('translation:popup_create_reminder.label_reminder_type')}
          placeholder={t('translation:popup_create_reminder.placeholder_reminder_type')}
          options={reminderType}
          itemProps={{ name: 'type', rules: [REQUIRED_RULE] }}
        />
        <Can I={ACTIONS.READ} a={UI.EDIT_RECRUITER_REMINDER} passThrough>
          {access => (
            <Select
              label={t('translation:popup_create_reminder.label_recruiter')}
              placeholder={t('translation:popup_create_reminder.placeholder_recruiter')}
              options={recruiterOptions}
              disabled={!access}
              itemProps={{ name: 'recruiter', rules: [REQUIRED_RULE] }}
              labelInValue={false}
              optionFilterProp="label"
              onChange={(v: RecruiterOption) => {
                form.setFieldValue('create_google_event', v.has_calendar_access);
              }}
            />
          )}
        </Can>
        <Select
          label={t('translation:popup_create_reminder.label_candidate')}
          placeholder={t('translation:popup_create_reminder.placeholder_candidate')}
          options={[]}
          disabled
          itemProps={{ name: 'candidate', rules: [REQUIRED_RULE] }}
        />
        <TextArea
          label={t('translation:popup_create_reminder.label_comment')}
          placeholder={t('translation:popup_create_reminder.placeholder_comment')}
          itemProps={{ name: 'comment' }}
        />
        <Form.Item shouldUpdate={(prev, curr) => prev.recruiter !== curr.recruiter} noStyle>
          {({ getFieldValue }) => {
            const disabled = !recruiterOptions.find(r => r.value === getFieldValue('recruiter'))?.has_calendar_access;
            return (
              <div className="reminder-calendar-checker">
                <Checkbox
                  disabled={disabled}
                  label={t('translation:popup_create_reminder.label_add_to_calendar')}
                  itemProps={{ name: 'create_google_event' }}
                />
                {disabled && (
                  <Tooltip label={t('translation:popup_create_reminder.tooltip_calendar_access')}>
                    <Info />
                  </Tooltip>
                )}
              </div>
            );
          }}
        </Form.Item>
      </Form>
    </Popup>
  );
};
